import { c as _c } from "react/compiler-runtime";
import { centsToUnits, CurrencyEnum } from '@systemeio/ui';
import { CloseIcon, InfoIcon } from '@systemeio/ui/icons';
import { useSessionStore } from '@systemeio/util';
import { useTotalRevenueLimit } from 'modules/main/hooks/use-total-revenue-limit';
import { useMemo, useState } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import TransLoco from 'shared/components/trans-loco';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
import { twJoin } from 'tailwind-merge';
const MIN_REVENUE_LIMIT_PERCENTAGE = 80;
const REVENUE_LIMIT_NOTIFICATION_CLOSED_KEY = 'revenueLimitNotificationClosed';
const getRevenueLimitAccentClassName = (percentage: number) => {
  if (percentage >= 95) {
    return 'text-[#B91C1C] fill-[#B91C1C]';
  }
  if (percentage >= 90) {
    return 'text-[#FF4C35] fill-[#FF4C35]';
  }
  return 'text-[#FACC15] fill-[#FACC15]';
};
export const RevenueLimitNotification = () => {
  const $ = _c(38);
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    totalRevenueLimit
  } = useTotalRevenueLimit();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      useSessionStorage: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const notificationState = useSessionStore(REVENUE_LIMIT_NOTIFICATION_CLOSED_KEY, t0);
  let t1;
  if ($[1] !== notificationState) {
    t1 = () => notificationState.getItem() === true;
    $[1] = notificationState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [isClosed, setIsClosed] = useState(t1);
  user?.dashboardLocale;
  let t2;
  bb0: {
    if (!totalRevenueLimit) {
      t2 = null;
      break bb0;
    }
    const revenueLimitInCents = totalRevenueLimit.maxLimit;
    const totalRevenueInCents = totalRevenueLimit.current;
    if (!revenueLimitInCents || totalRevenueInCents === null || totalRevenueInCents === undefined) {
      t2 = null;
      break bb0;
    }
    const percentage = Math.floor(totalRevenueInCents / revenueLimitInCents * 100);
    if (percentage < MIN_REVENUE_LIMIT_PERCENTAGE) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[3] !== totalRevenueInCents || $[4] !== user?.dashboardLocale) {
      t3 = currencyFormat(centsToUnits(totalRevenueInCents), CurrencyEnum.US_DOLLAR, user?.dashboardLocale, true);
      $[3] = totalRevenueInCents;
      $[4] = user?.dashboardLocale;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    const t4 = totalRevenueInCents >= revenueLimitInCents;
    let t5;
    if ($[6] !== revenueLimitInCents || $[7] !== user?.dashboardLocale) {
      t5 = currencyFormat(centsToUnits(revenueLimitInCents), CurrencyEnum.US_DOLLAR, user?.dashboardLocale, true);
      $[6] = revenueLimitInCents;
      $[7] = user?.dashboardLocale;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== percentage || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
      t6 = {
        amount: t3,
        isLimitReached: t4,
        limit: t5,
        percentage
      };
      $[9] = percentage;
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t2 = t6;
  }
  const notificationData = t2;
  let t3;
  if ($[14] !== notificationState) {
    t3 = () => {
      notificationState.setItem(true);
      setIsClosed(true);
    };
    $[14] = notificationState;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const handleClose = t3;
  if (!notificationData || isClosed && !notificationData.isLimitReached) {
    return null;
  }
  const {
    amount,
    isLimitReached,
    limit,
    percentage: percentage_0
  } = notificationData;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[16] !== amount || $[17] !== isLimitReached || $[18] !== limit || $[19] !== percentage_0 || $[20] !== t) {
    const accentClassName = getRevenueLimitAccentClassName(percentage_0);
    t7 = "border-fill-primary bg-background-secondary text-text-primary -mx-4 -mt-5 mb-5 border-y px-4 py-2.5 sm:-mx-6 sm:px-6 md:-mx-14 md:px-14";
    t6 = "flex items-center justify-between gap-4 text-sm";
    t4 = "flex items-start";
    t5 = isLimitReached ? <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification_reached" values={{
      maxLimit: limit
    }} components={{
      icon: <InfoIcon className={twJoin("mr-2 inline-block size-4", accentClassName)} />,
      span: <span className={accentClassName} />,
      a: <LinkWithoutPrefetch className="primary-link" href={getPageRoute(pageRoute.ProfileUserPlan)} />
    }} /> : <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification" values={{
      current: amount,
      maxLimit: limit
    }} components={{
      icon: <InfoIcon className={twJoin("mr-2 inline-block size-4", accentClassName)} />,
      percentage: <span className={accentClassName}>{percentage_0}%</span>,
      span: <span className={accentClassName} />,
      a: <LinkWithoutPrefetch className="primary-link" href={getPageRoute(pageRoute.ProfileUserPlan)} />
    }} />;
    $[16] = amount;
    $[17] = isLimitReached;
    $[18] = limit;
    $[19] = percentage_0;
    $[20] = t;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t4 || $[26] !== t5) {
    t8 = <span className={t4}>{t5}</span>;
    $[25] = t4;
    $[26] = t5;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== handleClose || $[29] !== isLimitReached) {
    t9 = !isLimitReached && <button aria-label="Close revenue limit notification" className="group flex size-8 shrink-0 items-center justify-center outline-hidden" onClick={handleClose} type="button"><CloseIcon className="main-transition-colors group-hover:fill-text-primary group-focus-visible:fill-text-primary fill-fill-primary size-4" /></button>;
    $[28] = handleClose;
    $[29] = isLimitReached;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t6 || $[32] !== t8 || $[33] !== t9) {
    t10 = <div className={t6}>{t8}{t9}</div>;
    $[31] = t6;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t7) {
    t11 = <div className={t7}>{t10}</div>;
    $[35] = t10;
    $[36] = t7;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
};